// Pawn. The line promotes within its first three moves by the side that
// gets it: `promotion`, or `underpromotion` when the pawn becomes anything
// but a queen. Otherwise the line's first move pushes a passed pawn (no
// enemy pawn ahead of it on its file or the files beside): `advancedPawn`
// when it reaches the seventh rank, `passedPawn` otherwise.
import type { Color, Square } from '@chess/api-model';
import type { Motif } from '@chess/api-mistakes';
import type { TacticLine } from './line.ts';
import { Board, fileOf, forward, moved, promotionRank, rankOf, squareAt } from './rays.ts';

/** How many of the side's moves of the line a promotion is looked for in. */
const PROMOTION_MOVES = 3;

/** Whether `colour`'s pawn on `square` is passed: no enemy pawn ahead on its file or the files beside. */
export function isPassed(board: Board, square: Square, colour: Color): boolean {
  const f = fileOf(square);
  const step = forward(colour);
  for (let r = rankOf(square) + step; r >= 0 && r < 8; r += step) {
    for (const df of [-1, 0, 1]) {
      const sq = squareAt(f + df, r);
      const p = sq ? board.get(sq) : undefined;
      if (p && p.kind === 'pawn' && p.colour !== colour) return false;
    }
  }
  return true;
}

export function pawn(line: TacticLine): Motif | undefined {
  const own = line.pv.filter((_, i) => i % 2 === 0).slice(0, PROMOTION_MOVES);
  const promotion = own.find((uci) => uci.length === 5);
  if (promotion) return { family: 'pawn', variation: promotion[4].toLowerCase() === 'q' ? 'promotion' : 'underpromotion' };
  const first = line.pv[0];
  const m = first ? moved(line.board, first) : undefined;
  if (!m || m.piece.kind !== 'pawn' || !isPassed(m.after, m.to, line.side)) return undefined;
  const seventh = promotionRank(line.side) - forward(line.side);
  return { family: 'pawn', variation: rankOf(m.to) === seventh ? 'advancedPawn' : 'passedPawn' };
}
